import Vue from 'vue';
import FilesProxy from '@/proxies/Files';
import FilesTransform from '@/transforms/Files';
import * as types from './mutation-types';

export default {
  async changeName({ dispatch }, payload) {
    const proxy = new FilesProxy();
    try {
      await proxy.changeName(payload);
      dispatch('notification/success', 'confirmationmessage.file_updated', { root: true })
    } catch (e) {
      dispatch('notification/error', ['errormessage.failed_to_change_file', { file_id: payload.id }], { root: true });
    }
  },
  async changeDetails({ dispatch }, payload) {
    const proxy = new FilesProxy();
    try {
      await proxy.changeDetails(payload);
      dispatch('refresh');
      dispatch('notification/success', 'confirmationmessage.file_updated', { root: true })
    } catch (e) {
      dispatch('notification/error', ['errormessage.failed_to_change_file', { file_id: payload.id }], { root: true });
    }
  },
  async changeClusterOutfits({ commit, dispatch }, payload) {
    try {
      commit(types.SET_CLUSTERS_LOADING, true);
      const proxy = new FilesProxy();
      const { clusters } = await proxy.changeClusters(payload);
      dispatch('setClusterOutfits', clusters);
      dispatch('notification/success', 'confirmationmessage.clusters_updated', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_update_clusters', { root: true });
    } finally {
      commit(types.SET_CLUSTERS_LOADING, false);
    }
  },
  async deleteLogs({ dispatch }, payload) {
    const proxy = new FilesProxy();
    try {
      await proxy.deleteLogs(payload);
      await dispatch('getLogs', { id: payload.job_id, isForce: true });
      dispatch('notification/success', 'confirmationmessage.logs_delete', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_delete_logs', { root: true });
    }
  },
  async getFrames({ dispatch, commit, getters }, payload) {
    try {
      commit(types.LOADING, true);
      const proxy = new FilesProxy();
      const { existing_frames, total_frames } = await proxy.getFrames(payload);
      const newItems = getters.items.map(el => {
        if (el.id === payload) {
          return {
            ...el,
            frames: {
              existing: existing_frames,
              total: total_frames,
            },
          }
        }
        return el;
      });
      commit(types.FILES, newItems);
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_get_frames_info', { root: true });
    } finally {
      commit(types.LOADING, false);
    }
  },
  async getLogs({ commit, dispatch, getters }, payload) {
    const { id, isForce } = payload;
    if (getters.logsId === id && getters.logsItems.length && !isForce) {
      return
    }
    if (getters.logsId !== id) {
      commit(types.RESET_LOGS_ITEMS);
    }

    try {
      commit(types.SET_LOGS_LOADING, true);
      const proxy = new FilesProxy();
      const { logs } = await proxy.getStatuses(id);
      commit(types.SET_LOGS, {
        ...getters.logs,
        id,
        items: logs,
      });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_get_logs', { root: true });
    } finally {
      commit(types.SET_LOGS_LOADING, false);
    }
  },
  async getClusters({ commit, dispatch, getters }, payload) {
    const { id } = payload;
    let page = payload.page || getters.clusterPage;
    const clusterId = getters.clusterId;
    if (clusterId === id && getters.clusterPage === page) {
      return;
    }

    commit(types.SET_CLUSTERS_LOADING, true);
    if (!!clusterId && id !== clusterId) {
      commit(types.RESET_CLUSTERS_ITEMS);
      page = 1;
    }

    try {
      const proxy = new FilesProxy();
      const { clusters, outfits } = await proxy.getClusters({
        id,
        limit: 20,
        page,
      });

      const items = { ...getters.clusterItems };
      for (const [key, elements] of Object.entries(clusters)) {
        items[key] = items[key]
          ? [...items[key], ...elements]
          : elements;
      }

      commit(types.SET_CLUSTERS, {
        ...getters.cluster,
        id,
        items,
        page,
        outfits,
      });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_get_clusters', { root: true });
    } finally {
      commit(types.SET_CLUSTERS_LOADING, false);
    }
  },
  setClusterOutfits({ commit }, payload) {
    commit(types.SET_CLUSTER_OUTFITS, payload);
  },
  async getItems({ commit, dispatch, getters }) {
    commit(types.LOADING, true);
    const proxy = new FilesProxy();
    try {
      const response = await proxy.getFiles(getters.sort);
      commit(types.FILES, FilesTransform.fetchCollection(response.files));
      commit(types.MORE, response.total > getters.items.length);
      commit(types.TOTAL, response.total);
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_get_files', { root: true });
      console.error('get files error: ${e.message}');
    } finally {
      commit(types.LOADING, false);
    }
  },
  async getItem({ commit, dispatch, getters }, payload) {
    commit(types.LOADING, true);
    const proxy = new FilesProxy();
    try {
      const response = await proxy.find(payload);
      const isExistFile = getters.items.find(({ id }) => id === response._id);
      if (isExistFile) {
        return
      }

      const formatFile = FilesTransform.fetch(response);
      commit(types.FILES, [...getters.items, formatFile]);
      commit(types.MORE, true);
    } catch (e) {
      Vue.router.push({ name: Vue.$app.route.FILES });
      dispatch('notification/error', ['errormessage.failed_to_get_file', { file_id: payload }], { root: true });
    } finally {
      commit(types.LOADING, false);
    }
  },
  async changeFps({ dispatch }, payload) {
    try {
      const proxy = new FilesProxy();
      await proxy.setFps(payload);
      dispatch('notification/success', 'confirmationmessage.fps_updated', { root: true });
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_update_fps', { root: true });
    }
  },
  async getPossibleFps({ commit, dispatch }, payload) {
    try {
      const proxy = new FilesProxy();
      const { allowed_fps } = await proxy.getPossibleFps(payload);
      commit(types.SET_POSSIBLE_FPS, allowed_fps);
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_get_fps', { root: true });
    }
  },
  addFile({ commit }, payload) {
    commit(types.FILES, [payload]);
  },
  async deleteItem({ commit, state }, payload) {
    const proxy = new FilesProxy();
    try {
      await proxy.delete(payload);
      commit(types.DELETE, payload);
      const deletionCount = Array.isArray(payload) ? payload.length : 1;
      commit(types.TOTAL, state.total - deletionCount);
    } catch (e) {
      // TODO
    }
  },
  sortItems({ commit, dispatch }, payload) {
    commit(types.SORT, payload);
    dispatch('getItems');
  },
  clearItems({ commit }) {
    commit(types.CLEAR_ITEMS);
  },
  resetFilter({ commit, dispatch, getters }) {
    commit(types.RESET_FILTER);
    dispatch('sortItems', getters.defaultSort);
  },
  async refresh({ commit, getters }) {
    const proxy = new FilesProxy();
    try {
      const response = await proxy.getFiles({ ...getters.sort, ...{ page: 1, limit: getters.items.length } });
      commit(types.FILES, FilesTransform.fetchCollection(response.files));
    } catch (e) {
      // TODO
    }
  },
  async refreshStatuses({ dispatch }) {
    if (!Vue.router.history.current.params.id) {
      dispatch('refresh');
    }
  },
  async startAnalyzeOutfits({ dispatch }, payload) {
    const proxy = new FilesProxy();
    try {
      await proxy.startAnalyzeOutfits(payload);
      await dispatch('getItems');
      dispatch('notification/success', 'confirmationmessage.analyze_detects', { root: true })
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_start_analyze_detects', { root: true });
    }
  },
  async updateFrames({ dispatch, commit }, payload) {
    try {
      commit(types.LOADING, true);
      const proxy = new FilesProxy();
      await proxy.updateFrames(payload);
      dispatch('notification/success', 'confirmationmessage.update_thumbnails_started', { root: true })
    } catch (e) {
      dispatch('notification/error', 'errormessage.failed_to_update_thumbnails', { root: true });
    } finally {
      commit(types.LOADING, false);
    }
  },
};
